// Shared selection + filter state for the Intelligence Board.
//
// Cross-linking is the killer pattern of this view: clicking a flight
// in the map filters the alerts and comms; clicking an alert focuses
// the map; clicking a driver in any panel surfaces their thread + their
// dot pulses on the map. Every panel reads `selected` and (selectively)
// writes to it.

import React, { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';

const IntelligenceCtx = createContext(null);

export function IntelligenceProvider({ children }) {
  // Unified selection — only one of these is "active" at a time.
  // shape: { kind: 'driver'|'flight'|'gate', id: string } | null
  const [selected, setSelected] = useState(null);

  // Map view state (zoom + pan); the map writes here, alerts call into
  // it via focusOnGate/focusOnDriver to programmatically navigate.
  const [mapView, setMapView] = useState({ zoom: 1, panX: 0, panY: 0 });

  // Demo time — advances on its own so the board feels alive.
  // Starts at 12:00 noon (matches mockAssignments DEMO_NOW_MIN).
  // Updates every second. The demoTick counter is bumped on the
  // same interval; cards memoize off it to advance bag scans /
  // hot-bag countdowns / etc.
  //
  // Replay mode pauses the live tick and lets the dispatcher scrub
  // back through past ticks. liveTick is the "now" anchor; viewTick
  // is what the UI is currently showing (== liveTick when not in
  // replay; an earlier value when scrubbing).
  const [demoTime, setDemoTime] = useState(12 * 60);
  const [liveTick, setLiveTick] = useState(0);
  const [replayActive, setReplayActive] = useState(false);
  const [viewTick, setViewTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      if (replayActive) return;  // pause clock during replay
      setDemoTime(t => t + 1 / 60);
      setLiveTick(t => {
        const nt = t + 1;
        setViewTick(nt);   // viewTick rides liveTick when not replaying
        return nt;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [replayActive]);

  const demoTick = viewTick;  // back-compat for live-detail consumers

  const startReplay = useCallback(() => {
    setReplayActive(true);
    setViewTick(liveTick);
  }, [liveTick]);
  const exitReplay = useCallback(() => {
    setReplayActive(false);
    setViewTick(liveTick);
  }, [liveTick]);
  const setReplayTick = useCallback((t) => {
    setViewTick(Math.max(0, Math.min(liveTick, t)));
  }, [liveTick]);

  // Hover state — separate from selected, lighter weight. Used for
  // tooltips and subtle highlights without commitment.
  const [hovered, setHovered] = useState(null);

  // Snoozed/dismissed alerts (live in context so dismissing in one
  // place updates the count up top).
  const [dismissedAlertIds, setDismissedAlertIds] = useState(() => new Set());
  const dismissAlert = useCallback((id) => {
    setDismissedAlertIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // ── Workspace: pinned + minimized cards ──────────────────────────
  // The dispatcher pins entities they want to keep an eye on while
  // they focus elsewhere. Pinned cards stack on the right column
  // even after the selection moves on. Minimized cards collapse to a
  // pill in the bottom dock.
  const [pinnedEntities, setPinnedEntities] = useState([]);    // [{kind, id}]
  const [minimizedKeys, setMinimizedKeys]   = useState(() => new Set()); // 'kind:id'

  const entityKey = (e) => `${e.kind}:${e.id}`;

  const isPinned = useCallback((entity) => {
    if (!entity) return false;
    const k = entityKey(entity);
    return pinnedEntities.some(p => entityKey(p) === k);
  }, [pinnedEntities]);

  const isMinimized = useCallback((entity) => {
    if (!entity) return false;
    return minimizedKeys.has(entityKey(entity));
  }, [minimizedKeys]);

  const togglePin = useCallback((entity) => {
    if (!entity) return;
    const k = entityKey(entity);
    setPinnedEntities(prev => {
      const exists = prev.some(p => entityKey(p) === k);
      if (exists) return prev.filter(p => entityKey(p) !== k);
      return [...prev, entity];
    });
    // Unpin → also un-minimize
    setMinimizedKeys(prev => {
      const next = new Set(prev);
      next.delete(k);
      return next;
    });
  }, []);

  const toggleMinimize = useCallback((entity) => {
    if (!entity) return;
    const k = entityKey(entity);
    setMinimizedKeys(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  }, []);

  const clearWorkspace = useCallback(() => {
    setPinnedEntities([]);
    setMinimizedKeys(new Set());
  }, []);

  // Map orientation — landscape (default, two horizontal rows) or
  // portrait (two vertical columns, vertical scroll). Toggle button
  // lives in the ContextBar.
  const [mapOrientation, setMapOrientation] = useState('landscape');
  const toggleMapOrientation = useCallback(() => {
    setMapOrientation(o => o === 'landscape' ? 'portrait' : 'landscape');
  }, []);

  // ── Annotations: dispatcher's persistent notes on entities ───────
  // Map<entityKey, [{id, text, createdAt}]>. Survives selection changes
  // until manually dismissed. Currently only gate notes are surfaced
  // visually (icon overlay on the gate); easy to extend to drivers.
  const [annotations, setAnnotations] = useState(() => new Map());
  const annotationsFor = useCallback((entity) => {
    if (!entity) return [];
    return annotations.get(entityKey(entity)) || [];
  }, [annotations]);
  const addAnnotation = useCallback((entity, text) => {
    if (!entity || !text?.trim()) return;
    const k = entityKey(entity);
    setAnnotations(prev => {
      const next = new Map(prev);
      const list = next.get(k) || [];
      next.set(k, [...list, { id: `n-${Date.now()}`, text: text.trim(), createdAt: new Date().toISOString() }]);
      return next;
    });
  }, []);
  const removeAnnotation = useCallback((entity, noteId) => {
    if (!entity) return;
    const k = entityKey(entity);
    setAnnotations(prev => {
      const next = new Map(prev);
      const list = (next.get(k) || []).filter(n => n.id !== noteId);
      if (list.length === 0) next.delete(k);
      else next.set(k, list);
      return next;
    });
  }, []);
  // Quick lookup: does the entity have any notes? (used for map badges)
  const hasAnnotations = useCallback((entity) => {
    if (!entity) return false;
    return (annotations.get(entityKey(entity))?.length || 0) > 0;
  }, [annotations]);

  const selectFlight = useCallback((id) => setSelected({ kind: 'flight', id }), []);
  const selectDriver = useCallback((id) => setSelected({ kind: 'driver', id }), []);
  const selectGate   = useCallback((id) => setSelected({ kind: 'gate',   id }), []);
  const clearSelection = useCallback(() => setSelected(null), []);

  // Programmatic map navigation (alerts call this when clicked)
  const focusOnGate = useCallback((gateId) => {
    setSelected({ kind: 'gate', id: gateId });
    // Map watches `selected` and re-centers itself.
  }, []);
  const focusOnDriver = useCallback((driverId) => {
    setSelected({ kind: 'driver', id: driverId });
  }, []);

  const value = useMemo(() => ({
    selected, setSelected,
    selectFlight, selectDriver, selectGate, clearSelection,
    focusOnGate, focusOnDriver,
    mapView, setMapView,
    demoTime, demoTick,
    liveTick, replayActive, startReplay, exitReplay, setReplayTick,
    hovered, setHovered,
    dismissedAlertIds, dismissAlert,
    pinnedEntities, isPinned, togglePin,
    minimizedKeys, isMinimized, toggleMinimize,
    clearWorkspace,
    mapOrientation, toggleMapOrientation,
    annotations, annotationsFor, addAnnotation, removeAnnotation, hasAnnotations,
  }), [
    selected, selectFlight, selectDriver, selectGate, clearSelection,
    focusOnGate, focusOnDriver,
    mapView, demoTime, demoTick, liveTick, replayActive, startReplay, exitReplay, setReplayTick,
    hovered, dismissedAlertIds, dismissAlert,
    pinnedEntities, isPinned, togglePin,
    minimizedKeys, isMinimized, toggleMinimize, clearWorkspace,
    mapOrientation, toggleMapOrientation,
    annotations, annotationsFor, addAnnotation, removeAnnotation, hasAnnotations,
  ]);

  return (
    <IntelligenceCtx.Provider value={value}>
      {children}
    </IntelligenceCtx.Provider>
  );
}

export function useIntel() {
  const ctx = useContext(IntelligenceCtx);
  if (!ctx) throw new Error('useIntel must be used inside <IntelligenceProvider>');
  return ctx;
}
